/**
 * Round 4 "Stores Management" — pagina master CRUD sedi cross-tenant.
 * Gating bottoni via viewer_permissions dal backend (permessi tenant_stores.*).
 * Lifecycle: sospendi (badge nel tenant, azioni bloccate) / archivia (sparisce
 * dal tenant) / riattiva / elimina solo se vuota (409 STORE_NOT_EMPTY con
 * elenco riferimenti).
 */
import { useState, useEffect, useCallback } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { SkeletonMetricGrid, SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@easyflow/ui-master/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-master/components/ui/alert-dialog';
import {
  Store, Building2, Users, RefreshCw, Search, MapPin, Activity,
  Plus, MoreHorizontal, Pencil, PauseCircle, Archive, PlayCircle, Trash2, PauseOctagon,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient, { ApiRequestError } from '@/lib/api';
import { toErr, PRODUCT_REGISTRY, DEFAULT_PRODUCT, getProductDefinition } from '@easyflow/shared';
import { StoreFormModalMaster, type MasterStoreDetail } from '@/components/StoreFormModalMaster';

type StoreStatus = 'active' | 'inactive' | 'suspended' | 'archived';

interface StoreData {
  id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  tenant_status: string;
  tenant_product?: string;
  company_id?: string | null;
  legal_name?: string | null;
  vat_number?: string | null;
  name: string;
  store_id: string | null;
  status: StoreStatus;
  sede_type?: 'punto_vendita' | 'ufficio';
  commercial_area_id?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  settings?: { timezone?: string } | null;
  /** Task #3563 — governance brand: org che gestisce la sede (NULL = tenant-managed). */
  managed_by_org_id: string | null;
  active_extensions: number;
  total_extensions: number;
  created_at: string;
  updated_at: string;
}

interface StoreStats {
  total_stores: number;
  active_stores: number;
  inactive_stores: number;
  suspended_stores: number;
  archived_stores: number;
  tenants_with_stores: number;
  total_active_extensions: number;
  total_extensions: number;
}

interface ViewerPermissions {
  can_create: boolean;
  can_update: boolean;
  can_lifecycle: boolean;
}

interface StoresListData {
  stores: StoreData[];
  viewer_permissions: ViewerPermissions;
}

interface StoreReference {
  table: string;
  count: number;
}

const noPermissions: ViewerPermissions = {
  can_create: false,
  can_update: false,
  can_lifecycle: false,
};

type ConfirmKind = 'suspend' | 'archive' | 'reactivate' | 'delete';

interface ConfirmState {
  kind: ConfirmKind;
  store: StoreData;
}

const CONFIRM_COPY: Record<ConfirmKind, { title: string; description: string; action: string; destructive: boolean }> = {
  suspend: {
    title: 'Sospendere la sede?',
    description: 'La sede resterà visibile nel tenant con badge "Sospesa" e le azioni di modifica saranno bloccate.',
    action: 'Sospendi',
    destructive: false,
  },
  archive: {
    title: 'Archiviare la sede?',
    description: 'La sede sparirà dalle liste del tenant. Potrà essere riattivata solo dal master.',
    action: 'Archivia',
    destructive: false,
  },
  reactivate: {
    title: 'Riattivare la sede?',
    description: 'La sede tornerà attiva e pienamente operativa nel tenant.',
    action: 'Riattiva',
    destructive: false,
  },
  delete: {
    title: 'Eliminare definitivamente la sede?',
    description: 'Operazione irreversibile, consentita solo se la sede non ha dati collegati (utenti, chiamate, magazzino, ecc.).',
    action: 'Elimina',
    destructive: true,
  },
};

function statusBadge(status: StoreStatus) {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Attiva</Badge>;
    case 'suspended':
      return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Sospesa</Badge>;
    case 'archived':
      return <Badge className="bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">Archiviata</Badge>;
    default:
      return <Badge variant="secondary">Inattiva</Badge>;
  }
}

const NATIVE_SELECT_CLASS =
  'px-3 py-2 border border-border dark:border-slate-600 rounded-md bg-white dark:bg-muted text-foreground dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500';

export default function StoresMaster() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [stores, setStores] = useState<StoreData[]>([]);
  const [stats, setStats] = useState<StoreStats | null>(null);
  const [perms, setPerms] = useState<ViewerPermissions>(noPermissions);
  const [searchTerm, setSearchTerm] = useState('');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | StoreStatus>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  // Modale create/edit.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<MasterStoreDetail | null>(null);
  // Conferme lifecycle.
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Esito DELETE bloccata: riferimenti che impediscono l'eliminazione.
  const [blockedRefs, setBlockedRefs] = useState<{ storeName: string; references: StoreReference[] } | null>(null);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [storesRes, statsRes] = await Promise.all([
        apiClient.get<StoresListData>('/master/stores'),
        apiClient.get<StoreStats>('/master/stores/stats'),
      ]);
      setStores(storesRes.data?.stores ?? []);
      setPerms(storesRes.data?.viewer_permissions ?? noPermissions);
      setStats(statsRes.data ?? null);
    } catch (error: unknown) {
      toast({
        title: 'Errore',
        description: toErr(error).message || 'Caricamento sedi fallito',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Tenant distinti presenti in lista, per il filtro.
  const tenantOptions = [...new Map(stores.map((s) => [s.tenant_id, { id: s.tenant_id, name: s.tenant_name, slug: s.tenant_slug }])).values()]
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredStores = stores.filter((store) => {
    if (tenantFilter !== 'all' && store.tenant_id !== tenantFilter) {
      return false;
    }
    if (productFilter !== 'all' && (store.tenant_product ?? DEFAULT_PRODUCT) !== productFilter) {
      return false;
    }
    if (statusFilter !== 'all' && store.status !== statusFilter) {
      return false;
    }
    const search = searchTerm.toLowerCase();
    return store.name.toLowerCase().includes(search) ||
      (store.store_id ?? '').toLowerCase().includes(search) ||
      store.tenant_name.toLowerCase().includes(search) ||
      (store.legal_name || '').toLowerCase().includes(search) ||
      (store.vat_number || '').toLowerCase().includes(search);
  });

  const openCreate = (): void => {
    setEditingStore(null);
    setIsModalOpen(true);
  };

  const openEdit = async (store: StoreData): Promise<void> => {
    setBusyId(store.id);
    try {
      // Dettaglio con brands per il form.
      const res = await apiClient.get<MasterStoreDetail>(`/master/stores/${store.id}`);
      if (!res.data) throw new Error('Sede non trovata');
      setEditingStore(res.data);
      setIsModalOpen(true);
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const runLifecycle = async ({ kind, store }: ConfirmState): Promise<void> => {
    setBusyId(store.id);
    try {
      if (kind === 'delete') {
        await apiClient.delete(`/master/stores/${store.id}`);
        toast({ title: 'Sede eliminata', description: `"${store.name}" è stata eliminata definitivamente.` });
      } else {
        const path = kind === 'suspend' ? 'suspend' : kind === 'archive' ? 'archive' : 'reactivate';
        await apiClient.post(`/master/stores/${store.id}/${path}`);
        const label = kind === 'suspend' ? 'sospesa' : kind === 'archive' ? 'archiviata' : 'riattivata';
        toast({ title: 'Operazione completata', description: `Sede "${store.name}" ${label}.` });
      }
      await loadData();
    } catch (err: unknown) {
      const body = err instanceof ApiRequestError
        ? (err.body as { code?: string; data?: { references?: StoreReference[] } } | null)
        : null;
      if (kind === 'delete' && body?.code === 'STORE_NOT_EMPTY' && Array.isArray(body.data?.references)) {
        setBlockedRefs({ storeName: store.name, references: body.data.references });
      } else {
        toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
      }
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-white/10 rounded animate-pulse" />
        </div>
        <SkeletonMetricGrid cols={4} />
        <SkeletonTable rows={10} />
      </div>
    );
  }

  const canActOn = perms.can_update || perms.can_lifecycle;

  return (
    <AnimatedPage>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Stores Management
            </h1>
            <p className="text-muted-foreground mt-1">Gestione cross-tenant delle sedi</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => void loadData()} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            {perms.can_create && (
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Nuova sede
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <GlassCard className="border-l-4 border-l-blue-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  Sedi totali
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{stats.total_stores}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-green-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Attive
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-green-600">{stats.active_stores}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-amber-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <PauseOctagon className="h-4 w-4" />
                  Sospese / Archiviate
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-amber-600">
                  {stats.suspended_stores} / {stats.archived_stores}
                </GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-purple-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Tenant con sedi
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{stats.tenants_with_stores}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-orange-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Interni attivi
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-orange-600">
                  {stats.total_active_extensions}
                </GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>
          </div>
        )}

        {/* Stores Table */}
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 h-4 w-4" />
                <Input
                  placeholder="Cerca per nome sede, codice, tenant, ragione sociale..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                className={`w-48 ${NATIVE_SELECT_CLASS}`}
              >
                <option value="all">Tutti i tenant</option>
                {tenantOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | StoreStatus)}
                className={`w-40 ${NATIVE_SELECT_CLASS}`}
              >
                <option value="all">Tutti gli stati</option>
                <option value="active">Attive</option>
                <option value="inactive">Inattive</option>
                <option value="suspended">Sospese</option>
                <option value="archived">Archiviate</option>
              </select>
              <select
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                className={`w-48 ${NATIVE_SELECT_CLASS}`}
              >
                <option value="all">Tutti i prodotti</option>
                {PRODUCT_REGISTRY.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sede</TableHead>
                    <TableHead>Codice</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Ragione sociale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-center">Interni</TableHead>
                    <TableHead>Creata</TableHead>
                    {canActOn && <TableHead className="text-right">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canActOn ? 8 : 7} className="text-center py-8 text-muted-foreground">
                        Nessuna sede trovata
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStores.map((store) => (
                      <TableRow key={store.id} className="hover:bg-muted/50 dark:hover:bg-white/5">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground/70" />
                            <div>
                              <div>{store.name}</div>
                              {(store.city || store.address) && (
                                <div className="text-xs text-muted-foreground">
                                  {[store.address, store.city].filter(Boolean).join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {store.store_id ? (
                            <code className="px-2 py-1 bg-muted rounded text-sm">{store.store_id}</code>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{store.tenant_name}</div>
                            <div className="text-sm text-muted-foreground">{store.tenant_slug}</div>
                            <Badge variant="outline" className="mt-0.5 w-fit text-[10px] px-1.5 py-0">
                              {getProductDefinition(store.tenant_product ?? DEFAULT_PRODUCT)?.label ?? store.tenant_product}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{store.legal_name || '—'}</div>
                          {store.vat_number && (
                            <div className="text-xs text-muted-foreground font-mono">{store.vat_number}</div>
                          )}
                        </TableCell>
                        <TableCell>{statusBadge(store.status)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-semibold text-green-600">
                              {store.active_extensions}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              / {store.total_extensions} totali
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {new Date(store.created_at).toLocaleDateString()}
                          </span>
                        </TableCell>
                        {canActOn && (
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" disabled={busyId === store.id} aria-label="Azioni sede">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {perms.can_update && (
                                  <DropdownMenuItem onClick={() => void openEdit(store)}>
                                    <Pencil className="h-4 w-4 mr-2" /> Modifica
                                  </DropdownMenuItem>
                                )}
                                {perms.can_lifecycle && (
                                  <>
                                    {perms.can_update && <DropdownMenuSeparator />}
                                    {store.status !== 'suspended' && store.status !== 'archived' && (
                                      <DropdownMenuItem onClick={() => setConfirm({ kind: 'suspend', store })}>
                                        <PauseCircle className="h-4 w-4 mr-2" /> Sospendi
                                      </DropdownMenuItem>
                                    )}
                                    {store.status !== 'archived' && (
                                      <DropdownMenuItem onClick={() => setConfirm({ kind: 'archive', store })}>
                                        <Archive className="h-4 w-4 mr-2" /> Archivia
                                      </DropdownMenuItem>
                                    )}
                                    {(store.status === 'suspended' || store.status === 'archived') && (
                                      <DropdownMenuItem onClick={() => setConfirm({ kind: 'reactivate', store })}>
                                        <PlayCircle className="h-4 w-4 mr-2" /> Riattiva
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => setConfirm({ kind: 'delete', store })}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" /> Elimina (solo se vuota)
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </GlassCardContent>
        </GlassCard>

        {/* Modale create/edit */}
        <StoreFormModalMaster
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setEditingStore(null); }}
          onSuccess={() => void loadData()}
          store={editingStore}
        />

        {/* Conferma lifecycle */}
        <AlertDialog open={confirm !== null} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm ? CONFIRM_COPY[confirm.kind].title : ''}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirm && (
                  <>
                    <span className="font-medium text-foreground">"{confirm.store.name}"</span>
                    {' '}({confirm.store.tenant_name}) — {CONFIRM_COPY[confirm.kind].description}
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                className={confirm && CONFIRM_COPY[confirm.kind].destructive
                  ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
                  : undefined}
                onClick={() => { if (confirm) void runLifecycle(confirm); }}
              >
                {confirm ? CONFIRM_COPY[confirm.kind].action : ''}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* DELETE bloccata: elenco riferimenti */}
        <AlertDialog open={blockedRefs !== null} onOpenChange={(open) => { if (!open) setBlockedRefs(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Sede non vuota</AlertDialogTitle>
              <AlertDialogDescription>
                Impossibile eliminare "{blockedRefs?.storeName}": sono presenti dati collegati.
                Archivia la sede oppure rimuovi prima i dati collegati.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {blockedRefs && (
              <div className="max-h-48 overflow-y-auto rounded-md border border-border/60 divide-y divide-border/40 text-sm">
                {blockedRefs.references.map((r) => (
                  <div key={r.table} className="flex items-center justify-between px-3 py-1.5">
                    <code className="text-xs">{r.table}</code>
                    <span className="text-xs text-muted-foreground">
                      {r.count >= 1000 ? '1000+' : r.count} {r.count === 1 ? 'riga' : 'righe'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setBlockedRefs(null)}>Ho capito</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AnimatedPage>
  );
}
